import { defineMessages } from 'react-intl';
import {
  hasOrchestration, hasReviewGroup, isReorderable, isReviewGroupLead,
} from '@printdeal/business-logic';
import type {
  ReOrderLineItem,
  GetOrdersResponse,
} from '../../../types/re-order/reOrder';

import LineItem from '../../../types/cart/LineItem';

export const InitialStateForRecentOrders = {
  count: 0,
  currentPage: 1,
  limit: 10,
  results: [],
  total: 0,
  totalPages: 0,
  allLineItems: [],
  lineItemsInViewAfterPagination: [],
  pagination: {
    activePage: 0,
  },
};

export const PAGINATION_CONFIG_REORDER_HOME = {
  PRODUCTS_PER_PAGE_PAGINATION: 8,
  LIMIT_ALL_RECENT_ORDERS: 30,
  PAGE_INDEX_FOR_ALL_RECENT_ORDERS: 1,
  ACTIVE_PAGE_LINE_ITEMS_FOR_INITIAL_LOAD: 1,
};

const filterInvalidLineItems = (lineItems: LineItem[]) => (lineItems.filter((item) => (isReorderable(item, true)
  && ((hasReviewGroup(item) && isReviewGroupLead(item)) || !hasReviewGroup(item)))
  && !hasOrchestration(item)));

const getValidLineItemsFromRecentOrders = (orders: GetOrdersResponse['message']): ReOrderLineItem[] => orders.results.map((order) => filterInvalidLineItems(order.lineItems)
  .map(
    (item) => ({ ...item, parentOrder: order }),
  )).flat();

const getItemsForPage = (
  allItems: ReOrderLineItem[],
  pageSize: number = PAGINATION_CONFIG_REORDER_HOME.PRODUCTS_PER_PAGE_PAGINATION,
  pageNumber: number = PAGINATION_CONFIG_REORDER_HOME.ACTIVE_PAGE_LINE_ITEMS_FOR_INITIAL_LOAD,
) => allItems.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

export const getNewStateForPageOrders = (recentOrdersData: GetOrdersResponse['message'], activePage: number) => {
  const allLineItemsFromAllOrders = getValidLineItemsFromRecentOrders(recentOrdersData);
  const lineItemsInViewForPaginatedView = getItemsForPage(
    allLineItemsFromAllOrders,
    PAGINATION_CONFIG_REORDER_HOME.PRODUCTS_PER_PAGE_PAGINATION,
    activePage,
  );

  return {
    ...recentOrdersData,
    pagination: { activePage },
    allLineItems: allLineItemsFromAllOrders,
    lineItemsInViewAfterPagination: lineItemsInViewForPaginatedView,
  };
};

export const reOrderMessages = defineMessages({
  reorderOrderNotFoundHeading: {
    id: 'home.reorder.notFoundHeading',
    defaultMessage: 'Reorder products',
  },
  reorderOrderNotFoundTitle: {
    id: 'home.reorder.notFoundTitle',
    defaultMessage: 'Unfortunately, we could not find any previous orders.',
  },
  reorderOrderNotFoundDescription: {
    id: 'home.reorder.notFoundMsg',
    defaultMessage: 'Place an order to enable reordering.',
  },
  reorderOrderNotFoundButton: {
    id: 'home.reorder.notFoundButton',
    defaultMessage: 'Request quote',
  },
  reOrderRecommendationsHeading: {
    id: 'home.reorder.notFoundRecommendationsHeading',
    defaultMessage: 'Popular products',
  },
  reorderReAssemleButtonText: {
    id: 'home.reorder.reAssembleButtonText',
    defaultMessage: 'Repeat with new design',
  },
  reorderAddToCartButtonText: {
    id: 'home.reorder.addtoCartButtonText',
    defaultMessage: 'Add to Cart',
  },
  reorderNavigationLinkText: {
    id: 'home.reorder.reorderNaigationTextinHeader',
    defaultMessage: 'Reorder',
  },
  reorderMainHeadingText: {
    id: 'home.reorder.mainHeading',
    defaultMessage: 'Reorder products',
  },
  reorderMainHeadingHelpText: {
    id: 'home.reorder.helptextMain',
    defaultMessage: 'Below you can easily add products that you ordered after 2021 to your shopping cart and order again',
  },
  reorderMainLoaderText: {
    id: 'home.reorder.mainloader',
    defaultMessage: 'Loading...',
  },
  reorderMainActualPrice: {
    id: 'home.reorder.actualPrice',
    defaultMessage: 'Actual Price excl. VAT: ',
  },
});
